/**
 * Personal: empleados, horas extra y vacaciones.
 *
 * Una hora extra aprobada es un dato de planilla: se asienta como costo y ya no
 * se toca. La pantalla lo refleja — después de aprobar no hay botón de editar.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  dateLabel, durationLabel, parseMoneyInput, periodLabel, today,
  VACATION_STATUS_LABEL, VACATION_STATUS_TONE, type VacationStatus,
} from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import { haptic } from '@/lib/haptics'; // HAPTIC_FEEDBACK
import {
  Async, Avatar, Badge, Button, Card, Cell, Empty, Field, Modal, Money, Note, PageHead, Stat, Table,
} from '@/components/ui';
import { PhotoUploader } from '@/components/common/PhotoUploader';
import { ScheduleEditor, scheduleIsValid, type ShiftDraft } from '@/components/common/ScheduleEditor';
import { InviteButton } from '@/components/common/InviteButton';
import '@/styles/agenda.css';

type Coverage = 'EMPLOYER_ONLY' | 'EMPLOYER_AND_WORKER' | 'NONE';

/** El horario resumido que ya viene calculado del Worker (0024). */
interface ScheduleSummary {
  days: number;
  weekly_minutes: number;
  /** Minutos efectivos por día, con 0 = domingo. */
  by_weekday: number[];
}

interface Employee {
  id: string;
  /** El perfil de acceso detrás del empleado; su cuenta cuelga de ahí. */
  profile_id: string | null;
  full_name: string | null; position: string | null; status: string;
  hire_date: string; base_salary_minor: number | null; pending_overtime: number;
  email: string | null; phone: string | null; photo_key: string | null;
  /** Ya entró y eligió su contraseña. Una temporal sin usar no cuenta. */
  account_active: number;
  identification: string | null; employee_code: string | null;
  employment_type: string; notes: string | null;
  social_charges_coverage: Coverage;
  /* `work_schedule_type` —el horario como frase— ya no está acá. Desde 0024 el
     horario es turno con descansos por día, y esta pantalla lo muestra
     calculado en `schedule`. La frase vieja se sigue pudiendo leer en el
     formulario de edición, rotulada como lo que es, pero nadie la escribe más:
     con dos lugares donde teclear el horario, tarde o temprano se contradicen. */
  schedule: ScheduleSummary;
  /** 0/1. Solo significa algo con `status = 'ON_LEAVE'` (0020). */
  leave_is_unpaid: number;
  leave_reason: string | null;
}

/** «5 días · 20 h» o el aviso de que esa persona no se puede programar. */
function scheduleLabel(schedule: ScheduleSummary | undefined): string {
  if (!schedule || schedule.days === 0) return 'Sin horario';
  return `${schedule.days} día${schedule.days === 1 ? '' : 's'} · ${durationLabel(schedule.weekly_minutes)}`;
}

/**
 * Qué cubre Clean Blue de las cargas sociales de esta persona.
 *
 * La obrera se le retiene al trabajador; que la asuma la empresa es la
 * excepción, y varía por empleado — por eso se marca uno por uno.
 */
const COVERAGE_LABEL: Record<Coverage, string> = {
  EMPLOYER_ONLY: 'Solo patronal',
  EMPLOYER_AND_WORKER: 'Patronal + obrera',
  NONE: 'Ninguna',
};

const COVERAGE_HINT: Record<Coverage, string> = {
  EMPLOYER_ONLY: 'La carga obrera se le retiene al trabajador de su bruto.',
  EMPLOYER_AND_WORKER: 'Clean Blue también asume la carga obrera de esta persona.',
  NONE: 'No se calcula ni patronal ni obrera. Para quien no pasa por el circuito de CCSS de la planilla.',
};

const COVERAGE_TONE: Record<Coverage, 'info' | 'neutral' | 'warn'> = {
  EMPLOYER_ONLY: 'neutral',
  EMPLOYER_AND_WORKER: 'info',
  NONE: 'warn',
};

const EMPLOYMENT_TYPE_LABEL: Record<string, string> = {
  FULL_TIME: 'Tiempo completo', PART_TIME: 'Medio tiempo', HOURLY: 'Por hora', CONTRACTOR: 'Contratista',
};

const EMPLOYEE_STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Activo', ON_LEAVE: 'Con permiso', SUSPENDED: 'Suspendido',
};

/**
 * Cómo se lee el estado en la fila, con el matiz del permiso.
 *
 * «Con permiso» a secas no dice lo único que importa para la planilla: si la
 * persona cobra el mes. Un permiso de un día no interrumpe el salario —es lo
 * normal y por eso es el default—; una licencia sin goce sí, y entonces tiene
 * que verse desde la lista, no solo dentro del formulario.
 */
function statusLabel(e: Pick<Employee, 'status' | 'leave_is_unpaid'>): string {
  if (e.status === 'ON_LEAVE') {
    return e.leave_is_unpaid ? 'Permiso sin goce' : 'Con permiso (con goce)';
  }
  return EMPLOYEE_STATUS_LABEL[e.status] ?? e.status;
}

interface Overtime {
  id: string; employee_name: string | null; work_date: string; hours: number;
  status: string; amount_minor: number | null; reason: string; unit_code: string | null;
}

interface Vacation {
  id: string; employee_name: string | null; start_date: string; end_date: string;
  days: number; status: VacationStatus; notes: string | null;
}

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'danger' | 'neutral' | 'info'> = {
  APPROVED: 'ok', PAID: 'ok', REPORTED: 'warn', PENDING_APPROVAL: 'warn',
  REJECTED: 'danger', REQUESTED: 'warn', CANCELLED: 'neutral',
  ACTIVE: 'ok', TERMINATED: 'neutral', ON_LEAVE: 'info', SUSPENDED: 'danger',
};

/**
 * La pestaña con la que abre la pantalla.
 *
 * Existe para que Obligaciones pueda mandar a correr la planilla de un mes
 * concreto: la agenda avisa que toca, y el enlace deja al dueño exactamente
 * donde se corre, en vez de pedirle que la busque. El nombre viaja en español
 * porque es una URL que una persona puede leer y compartir.
 */
function tabFromQuery(raw: string | null): 'people' | 'overtime' | 'vacations' | 'payroll' {
  return raw === 'planilla' ? 'payroll'
    : raw === 'extras' ? 'overtime'
      : raw === 'vacaciones' ? 'vacations'
        : 'people';
}

/**
 * Lista o galería, el mismo conmutador que en Doctores y con el mismo
 * componente: `.segmented` y `localStorage`. No hay un tercer patrón de
 * conmutador en el admin.
 *
 * **Acá el default es la lista y en Doctores es la galería.** No es un
 * descuido: en Doctores lo que la tabla lleva son datos de contacto, y la cara
 * identifica a una persona mejor que su nombre. En Personal la tabla lleva
 * salario, cargas sociales y estado de planilla — números que se comparan
 * entre filas y que una grilla de tarjetas no puede alinear. Quien prefiera la
 * galería la elige y se le queda guardada; abrir escondiendo la planilla
 * sería peor que no tener el conmutador.
 */
type StaffView = 'gallery' | 'list';
const STAFF_VIEW_KEY = 'cleanblue:staff-view';

const STAFF_VIEW_OPTIONS: { key: StaffView; label: string }[] = [
  { key: 'gallery', label: 'Galería' },
  { key: 'list', label: 'Lista' },
];

/** El conmutador, en su propio componente porque se renderiza igual en las dos
 *  ramas del ternario y duplicarlo era pedir que se desincronizaran. */
function StaffViewSwitch({ view, onChange }: { view: StaffView; onChange: (v: StaffView) => void }) {
  return (
    <div className="row mb-2">
      <span className="spacer" />
      <div
        /* Sin `segmented--view`: esa variante se esconde bajo 760px porque en
           Doctores la galería es obligatoria en teléfono. Acá no hay vista
           forzada por breakpoint, así que la elección tiene que seguir
           disponible en pantalla chica — si no, quien guardó «galería» no
           tendría cómo volver a la tabla desde el celular. */
        className="segmented"
        role="group"
        aria-label="Cómo ver el personal"
        style={{ '--seg-n': STAFF_VIEW_OPTIONS.length, '--seg-i': STAFF_VIEW_OPTIONS.findIndex((v) => v.key === view) } as React.CSSProperties}
      >
        <div className="segmented__pill" />
        {STAFF_VIEW_OPTIONS.map((v) => (
          <button
            key={v.key}
            type="button"
            aria-pressed={view === v.key}
            className={`segmented__opt${view === v.key ? ' is-active' : ''}`}
            onClick={() => { if (view !== v.key) haptic.light(); onChange(v.key); }} // HAPTIC_FEEDBACK
          >
            {v.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function StaffPage() {
  const [params] = useSearchParams();
  // Solo el estado INICIAL: después manda la pestaña que el usuario elija, sin
  // que la URL se lo discuta.
  const [tab, setTab] = useState<'people' | 'overtime' | 'vacations' | 'payroll'>(
    () => tabFromQuery(params.get('tab')));
  const employees = useApi<{ employees: Employee[] }>('/admin/employees');
  const overtime = useApi<{ overtime: Overtime[] }>('/admin/overtime', { enabled: tab === 'overtime' });
  const vacations = useApi<{ vacations: Vacation[] }>('/admin/vacations', { enabled: tab === 'vacations' });
  const [creating, setCreating] = useState(false);
  const [photoFor, setPhotoFor] = useState<Employee | null>(null);
  const [editing, setEditing] = useState<Employee | null>(null);
  const [view, setView] = useState<StaffView>(
    () => (localStorage.getItem(STAFF_VIEW_KEY) === 'gallery' ? 'gallery' : 'list'),
  );

  function changeView(next: StaffView) {
    setView(next);
    localStorage.setItem(STAFF_VIEW_KEY, next);
  }

  const pending = employees.data?.employees.reduce((a, e) => a + e.pending_overtime, 0) ?? 0;

  const decideOvertime = useApiMutation(
    ({ id, action }: { id: string; action: 'approve' | 'reject' }) =>
      api.post(`/admin/overtime/${id}/${action}`, action === 'reject' ? { reason: 'No corresponde' } : {}),
    ['/admin/overtime', '/admin/employees', '/admin/transactions'],
    { onSuccess: (_data, vars) => (vars.action === 'approve' ? haptic.success() : haptic.warning()) }, // HAPTIC_FEEDBACK
  );

  const decideVacation = useApiMutation(
    ({ id, decision }: { id: string; decision: 'APPROVED' | 'REJECTED' }) =>
      api.post(`/admin/vacations/${id}/decide`, { decision }),
    ['/admin/vacations'],
    { onSuccess: (_data, vars) => (vars.decision === 'APPROVED' ? haptic.success() : haptic.warning()) }, // HAPTIC_FEEDBACK
  );

  return (
    <>
      <PageHead
        title="Personal"
        subtitle="Los empleados no se borran: quedan como terminados, con su fecha."
        actions={<Button variant="primary" onClick={() => setCreating(true)}>Nuevo empleado</Button>}
      />

      <div className="tabs mb-2">
        <button type="button" className={`tab${tab === 'people' ? ' is-active' : ''}`} aria-pressed={tab === 'people'} onClick={() => { if (tab !== 'people') { haptic.light(); setTab('people'); } }}> {/* HAPTIC_FEEDBACK */}
          Empleados
        </button>
        <button type="button" className={`tab${tab === 'overtime' ? ' is-active' : ''}`} aria-pressed={tab === 'overtime'} onClick={() => { if (tab !== 'overtime') { haptic.light(); setTab('overtime'); } }}> {/* HAPTIC_FEEDBACK */}
          Horas extra {pending > 0 && `(${pending})`}
        </button>
        <button type="button" className={`tab${tab === 'vacations' ? ' is-active' : ''}`} aria-pressed={tab === 'vacations'} onClick={() => { if (tab !== 'vacations') { haptic.light(); setTab('vacations'); } }}> {/* HAPTIC_FEEDBACK */}
          Vacaciones
        </button>
        <button type="button" className={`tab${tab === 'payroll' ? ' is-active' : ''}`} aria-pressed={tab === 'payroll'} onClick={() => { if (tab !== 'payroll') { haptic.light(); setTab('payroll'); } }}> {/* HAPTIC_FEEDBACK */}
          Planilla
        </button>
      </div>

      {tab === 'people' && (
        <Async query={employees} empty={(d) => d.employees.length === 0 && (
          <Empty icon="incidencias" title="Todavía no hay personal cargado" />
        )}>
          {(data) => view === 'gallery' ? (
            <>
              <StaffViewSwitch view={view} onChange={changeView} />
              <div className="person-grid">
                {data.employees.map((e) => (
                  <div key={e.id} className="person-card">
                    {/* El avatar sigue siendo el botón para cambiar la foto,
                        igual que en la tabla: es la misma acción, en la misma
                        figura, y no hay que aprenderla dos veces. */}
                    <button
                      type="button"
                      className="avatar-button"
                      onClick={() => setPhotoFor(e)}
                      title="Cambiar foto"
                      aria-label={`Cambiar foto de ${e.full_name ?? 'este empleado'}`}
                    >
                      <Avatar
                        src={e.photo_key ? `/api/admin/employees/${e.id}/photo?v=${e.photo_key}` : null}
                        name={e.full_name ?? '?'}
                        size={56}
                        lazy
                      />
                    </button>
                    <span className="person-card__name">{e.full_name ?? '—'}</span>
                    <div className="person-card__where">{e.position ?? 'Sin puesto'}</div>
                    <div className="person-card__contact">{scheduleLabel(e.schedule)}</div>
                    <div className="person-card__contact">Desde {dateLabel(e.hire_date)}</div>
                    <div className="person-card__meta">
                      <Badge
                        tone={e.status === 'ON_LEAVE' && e.leave_is_unpaid
                          ? 'warn' : (STATUS_TONE[e.status] ?? 'neutral')}
                        title={e.leave_reason ?? undefined}
                      >
                        {statusLabel(e)}
                      </Badge>
                    </div>
                    <div className="person-card__action">
                      <Button variant="ghost" onClick={() => setEditing(e)}>Editar perfil</Button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
            <StaffViewSwitch view={view} onChange={changeView} />
            <Card flush>
              <Table head={
                <tr>
                  <th /><th className="tbl--anchor">Nombre</th><th>Puesto</th><th>Horario</th><th>Desde</th>
                  <th className="right">Salario</th>
                  <th>Cargas</th><th>Estado</th><th />
                </tr>
              }>
                {data.employees.map((e) => (
                  <tr key={e.id}>
                    <td>
                      <button
                        type="button"
                        className="avatar-button"
                        onClick={() => setPhotoFor(e)}
                        title="Cambiar foto"
                        aria-label={`Cambiar foto de ${e.full_name ?? 'este empleado'}`}
                      >
                        <Avatar
                          src={e.photo_key ? `/api/admin/employees/${e.id}/photo?v=${e.photo_key}` : null}
                          name={e.full_name ?? '?'}
                        />
                      </button>
                    </td>
                    <td className="strong tbl--anchor"><Cell sub={e.phone ?? e.email}>{e.full_name ?? '—'}</Cell></td>
                    <td>{e.position ?? '—'}</td>
                    {/* Sin horario declarado no es un dato faltante cualquiera:
                        es una persona a la que el programador no puede
                        asignarle ninguna visita. Por eso va marcado, no en
                        gris junto a los demás vacíos. */}
                    <td className="small">
                      {e.schedule?.days
                        ? <span className="muted">{scheduleLabel(e.schedule)}</span>
                        : <Badge tone="warn" title="Sin turno declarado no se le pueden asignar visitas">Sin horario</Badge>}
                    </td>
                    <td className="small muted">{dateLabel(e.hire_date)}</td>
                    <td className="right"><Money value={e.base_salary_minor} dim /></td>
                    <td>
                      <Badge
                        tone={COVERAGE_TONE[e.social_charges_coverage]}
                        title={COVERAGE_HINT[e.social_charges_coverage]}
                      >
                        {COVERAGE_LABEL[e.social_charges_coverage]}
                      </Badge>
                    </td>
                    <td>
                      <Badge
                        tone={e.status === 'ON_LEAVE' && e.leave_is_unpaid
                          ? 'warn' : (STATUS_TONE[e.status] ?? 'neutral')}
                        title={e.leave_reason ?? undefined}
                      >
                        {statusLabel(e)}
                      </Badge>
                      {e.status === 'ON_LEAVE' && e.leave_is_unpaid === 1 && (
                        <span className="tbl__sub">No entra en la planilla del mes</span>
                      )}
                    </td>
                    <td className="right"><Button onClick={() => setEditing(e)}>Editar perfil</Button></td>
                  </tr>
                ))}
              </Table>
            </Card>
            </>
          )}
        </Async>
      )}

      {tab === 'overtime' && (
        <Async query={overtime} empty={(d) => d.overtime.length === 0 && (
          <Empty icon="confirmar" title="Sin horas extra reportadas" />
        )}>
          {(data) => (
            <Card flush>
              <Table head={
                <tr>
                  <th className="tbl--anchor">Empleado</th><th>Fecha</th><th className="right">Horas</th>
                  <th>Motivo</th><th className="right">Monto</th><th>Estado</th><th />
                </tr>
              }>
                {data.overtime.map((o) => (
                  <tr key={o.id}>
                    <td className="strong tbl--anchor">{o.employee_name ?? '—'}</td>
                    <td className="small muted">{dateLabel(o.work_date)}</td>
                    <td className="right">{o.hours}</td>
                    <td><Cell sub={o.unit_code}>{o.reason}</Cell></td>
                    <td className="right"><Money value={o.amount_minor} dim={o.amount_minor == null} /></td>
                    <td><Badge tone={STATUS_TONE[o.status] ?? 'neutral'}>{o.status}</Badge></td>
                    <td className="right">
                      {(o.status === 'REPORTED' || o.status === 'PENDING_APPROVAL') && (
                        <>
                          <Button disabled={decideOvertime.isPending}
                            onClick={() => decideOvertime.mutate({ id: o.id, action: 'reject' })}>
                            Rechazar
                          </Button>{' '}
                          <Button variant="primary" disabled={decideOvertime.isPending}
                            onClick={() => decideOvertime.mutate({ id: o.id, action: 'approve' })}>
                            Aprobar
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </Table>
            </Card>
          )}
        </Async>
      )}

      {tab === 'vacations' && (
        <Async query={vacations} empty={(d) => d.vacations.length === 0 && (
          <Empty icon="horas" title="Sin solicitudes de vacaciones" />
        )}>
          {(data) => (
            <Card flush>
              <Table head={
                <tr><th className="tbl--anchor">Empleado</th><th>Desde</th><th>Hasta</th><th className="right">Días</th><th>Estado</th><th /></tr>
              }>
                {data.vacations.map((v) => (
                  <tr key={v.id}>
                    <td className="strong tbl--anchor"><Cell sub={v.notes}>{v.employee_name ?? '—'}</Cell></td>
                    <td className="small">{dateLabel(v.start_date)}</td>
                    <td className="small">{dateLabel(v.end_date)}</td>
                    <td className="right">{v.days}</td>
                    <td><Badge tone={VACATION_STATUS_TONE[v.status]}>{VACATION_STATUS_LABEL[v.status]}</Badge></td>
                    <td className="right">
                      {v.status === 'REQUESTED' && (
                        <>
                          <Button onClick={() => decideVacation.mutate({ id: v.id, decision: 'REJECTED' })}>
                            Rechazar
                          </Button>{' '}
                          <Button variant="primary"
                            onClick={() => decideVacation.mutate({ id: v.id, decision: 'APPROVED' })}>
                            Aprobar
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </Table>
            </Card>
          )}
        </Async>
      )}

      {tab === 'payroll' && <PayrollTab initialPeriod={params.get('period')} />}

      {creating && <NewEmployeeModal onClose={() => setCreating(false)} />}
      {photoFor && <EmployeePhotoModal employee={photoFor} onClose={() => setPhotoFor(null)} />}
      {editing && <EditEmployeeModal employee={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function EmployeePhotoModal({ employee, onClose }: { employee: Employee; onClose: () => void }) {
  return (
    <Modal title={`Foto · ${employee.full_name ?? 'Empleado'}`} onClose={onClose} footer={<Button onClick={onClose}>Cerrar</Button>}>
      <PhotoUploader
        uploadPath={`/admin/employees/${employee.id}/photo`}
        deletePath={`/admin/employees/${employee.id}/photo`}
        photoSrc={employee.photo_key
          ? `/api/admin/employees/${employee.id}/photo?v=${employee.photo_key}` : null}
        name={employee.full_name ?? '?'}
        invalidate={['/admin/employees']}
        size={72}
      />
    </Modal>
  );
}

/**
 * Edita el perfil completo — no solo la foto. `PATCH /admin/employees/:id`
 * reparte los campos entre `profiles` (nombre/correo/teléfono) y `employees`
 * (lo laboral) del lado del servidor; acá se manda todo junto.
 */
function EditEmployeeModal({ employee, onClose }: { employee: Employee; onClose: () => void }) {
  const [form, setForm] = useState({
    full_name: employee.full_name ?? '',
    position: employee.position ?? '',
    phone: employee.phone ?? '',
    email: employee.email ?? '',
    identification: employee.identification ?? '',
    employee_code: employee.employee_code ?? '',
    base_salary: employee.base_salary_minor != null ? String(employee.base_salary_minor / 100) : '',
    notes: employee.notes ?? '',
    leave_reason: employee.leave_reason ?? '',
    // Al crear no se puede volver a tocar: un contrato firmado unos días
    // después del primer día real, o un error de tipeo, quedaban sin forma
    // de corregirse — y esta es la fecha que decide las vacaciones por ley.
    hire_date: employee.hire_date,
  });
  const isTerminated = employee.status === 'TERMINATED';
  const [employmentType, setEmploymentType] = useState(employee.employment_type);
  const [coverage, setCoverage] = useState<Coverage>(employee.social_charges_coverage);
  const [status, setStatus] = useState<'ACTIVE' | 'ON_LEAVE' | 'SUSPENDED'>(
    isTerminated ? 'ACTIVE' : (employee.status as 'ACTIVE' | 'ON_LEAVE' | 'SUSPENDED'),
  );
  const [leaveIsUnpaid, setLeaveIsUnpaid] = useState(employee.leave_is_unpaid === 1);

  /* «Para dar de baja, usá la opción de terminar el contrato» — el hint ya lo
     prometía, pero el botón nunca se conectó (el endpoint existe desde 0069
     y no tenía de dónde llamarse). No se borra a nadie: queda TERMINATED con
     su fecha, como dice el subtítulo de la pantalla. */
  const [terminating, setTerminating] = useState(false);
  const [terminationDate, setTerminationDate] = useState(today());
  const terminate = useApiMutation(
    () => api.post(`/admin/employees/${employee.id}/terminate`, { termination_date: terminationDate }),
    ['/admin/employees', '/admin/payroll'],
    { onSuccess: onClose },
  );

  /* El horario tiene su propio endpoint porque tiene su propia semántica: no
     se pisa, se cierra desde una fecha. Por eso viaja aparte del PATCH, y por
     eso hay un campo «rige desde». */
  const schedule = useApi<{ shifts: ShiftDraft[]; legacy_note: string | null }>(
    `/admin/employees/${employee.id}/schedule`);
  /* `null` significa «no se tocó». Distinguirlo de «se vació» importa: guardar
     sin haber abierto el horario no puede borrarle el turno a nadie. */
  const [shiftDraft, setShiftDraft] = useState<ShiftDraft[] | null>(null);
  const [validFrom, setValidFrom] = useState(today());
  const shifts = shiftDraft ?? schedule.data?.shifts ?? [];

  const mutation = useApiMutation(
    async () => {
      await api.patch(`/admin/employees/${employee.id}`, {
      full_name: form.full_name,
      position: form.position || undefined,
      phone: form.phone || undefined,
      email: form.email || undefined,
      identification: form.identification || undefined,
      employee_code: form.employee_code || undefined,
      employment_type: employmentType,
      // Un empleado ya terminado no puede reactivarse guardando otro campo
      // cualquiera de este mismo formulario — no mandamos status en ese caso.
      status: isTerminated ? undefined : status,
      // Solo tiene sentido con el permiso puesto; el servidor la apaga sola en
      // cualquier otro estado.
      leave_is_unpaid: status === 'ON_LEAVE' ? leaveIsUnpaid : undefined,
      leave_reason: status === 'ON_LEAVE' && leaveIsUnpaid
        ? (form.leave_reason || null) : undefined,
      notes: form.notes || undefined,
      base_salary: form.base_salary ? parseMoneyInput(form.base_salary) ?? undefined : undefined,
      hire_date: form.hire_date !== employee.hire_date ? form.hire_date : undefined,
      social_charges_coverage: coverage,
      });

      /* Solo si se tocó. Y después del PATCH, no antes: si el perfil falla, el
         horario no se movió tampoco. */
      if (shiftDraft) {
        await api.put(`/admin/employees/${employee.id}/schedule`, {
          valid_from: validFrom,
          shifts: shiftDraft,
        });
      }
    },
    // La planilla se calcula con el salario y con quién asume las cargas: si se
    // editan acá, la previsualización abierta tiene que dejar de ser vieja.
    ['/admin/employees', '/admin/payroll'],
    { onSuccess: onClose },
  );
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Modal
      title={`Editar perfil · ${employee.full_name ?? 'Empleado'}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary"
            disabled={!form.full_name || mutation.isPending || !scheduleIsValid(shifts)}
            onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        </>
      }
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}

      <Field label="Foto">
        <PhotoUploader
          uploadPath={`/admin/employees/${employee.id}/photo`}
          deletePath={`/admin/employees/${employee.id}/photo`}
          photoSrc={employee.photo_key
            ? `/api/admin/employees/${employee.id}/photo?v=${employee.photo_key}` : null}
          name={employee.full_name ?? '?'}
          invalidate={['/admin/employees']}
          size={64}
        />
      </Field>

      <Field label="Nombre completo" required>
        <input className="input" value={form.full_name} onChange={set('full_name')} />
      </Field>
      <div className="grid grid--2">
        <Field label="Teléfono"><input className="input" value={form.phone} onChange={set('phone')} /></Field>
        <Field label="Correo"><input className="input" type="email" value={form.email} onChange={set('email')} /></Field>
      </div>

      {/* El correo de arriba es su usuario; esto le entrega la llave. Mismo
          camino único que los clientes: se le manda el enlace y elige su
          contraseña. Solo mientras no haya entrado —`account_active` mira
          `must_change = 0`, no si existe credencial— y solo si tiene correo
          guardado, que es lo que el servidor exige. El correo que se usa es el
          de la base, no el del formulario: si lo acaba de teclear y todavía no
          guardó, el enlace saldría a nombre de algo que el servidor no conoce.
          El botón abre su propio modal —va por portal— así que convive con
          este formulario. */}
      {employee.profile_id && !isTerminated && !employee.account_active && (
        <Field
          label="Acceso al portal del empleado"
          hint="Le manda un enlace para que elija su propia contraseña. Vence en una semana."
        >
          {employee.email ? (
            <InviteButton
              profileId={employee.profile_id}
              personName={employee.full_name ?? 'esta persona'}
              email={employee.email}
            />
          ) : (
            <p className="small muted">
              Guardale un correo primero: es su nombre de usuario para entrar.
            </p>
          )}
        </Field>
      )}

      <Field label="Puesto"><input className="input" value={form.position} onChange={set('position')} /></Field>

      {/* El horario. Antes de 0024 era un `<input>` de texto libre con el hint
          «Ej. lunes a viernes, 6 a.m.–2 p.m.»: servía para leerlo, no para
          calcular nada. Ahora es turno con descansos por día, y de acá sale la
          capacidad que el programador necesita. */}
      <Field
        label="Horario de trabajo"
        hint="La capacidad en minutos se calcula sola; los descansos se restan."
      >
        <ScheduleEditor shifts={shifts} onChange={setShiftDraft} />
      </Field>

      {/* Cambiar el horario NO reescribe el pasado: las visitas ya ocurridas se
          siguen explicando con el turno que regía ese día. La fecha es lo que
          hace posible esa garantía, así que se muestra, no se asume. */}
      {shiftDraft && (
        <Field label="El horario nuevo rige desde"
          hint="Lo anterior queda archivado con su vigencia: las visitas ya ocurridas no cambian.">
          <input className="input" type="date" value={validFrom}
            onChange={(e) => setValidFrom(e.target.value)} />
        </Field>
      )}

      {/* La frase que alguien escribió antes de que el horario fuera
          estructurado. Se muestra —puede decir algo que el turno no
          representa— y no se puede editar: un solo lugar donde teclear el
          horario. */}
      {schedule.data?.legacy_note && (
        <Field label="Nota de horario anterior"
          hint="Se escribió antes de que el horario fuera estructurado. Queda como referencia y ya no se edita.">
          <div className="shift-legacy">{schedule.data.legacy_note}</div>
        </Field>
      )}

      <div className="grid grid--2">
        <Field label="Tipo de jornada">
          <select className="select" value={employmentType}
            onChange={(e) => setEmploymentType(e.target.value)}>
            {Object.entries(EMPLOYMENT_TYPE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <Field label="Estado" hint={isTerminated
          ? 'Este empleado ya está terminado. Este formulario no lo reactiva.'
          : 'Para dar de baja, usá la opción de terminar el contrato.'}>
          {isTerminated ? (
            <input className="input" value="Terminado" disabled />
          ) : (
            <select className="select" value={status}
              onChange={(e) => setStatus(e.target.value as typeof status)}>
              {Object.entries(EMPLOYEE_STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          )}
        </Field>
      </div>

      {!isTerminated && (
        terminating ? (
          <Note tone="warn">
            <div className="grid grid--2" style={{ alignItems: 'end', gap: 10 }}>
              <Field label="Fecha de salida">
                <input className="input" type="date" value={terminationDate}
                  onChange={(e) => setTerminationDate(e.target.value)} />
              </Field>
              <div className="row" style={{ gap: 8 }}>
                <Button onClick={() => setTerminating(false)}>Cancelar</Button>
                <Button variant="danger" disabled={terminate.isPending}
                  onClick={() => terminate.mutate()}>
                  {terminate.isPending ? 'Terminando…' : 'Confirmar salida'}
                </Button>
              </div>
            </div>
            {terminate.error && <p className="small" style={{ marginTop: 8 }}>{terminate.error.message}</p>}
            <p className="small muted" style={{ marginTop: 8, marginBottom: 0 }}>
              No se borra: queda como terminado, con esta fecha, y pierde el acceso al portal.
            </p>
          </Note>
        ) : (
          <Button variant="danger" onClick={() => setTerminating(true)}>
            Terminar contrato
          </Button>
        )
      )}

      {/* El matiz del permiso. Un permiso de un día no interrumpe el salario
          —por eso la persona sigue en la planilla salvo que se marque acá— y
          una licencia larga sin goce sí. Solo aparece con el permiso puesto:
          fuera de ese estado la casilla no significa nada. */}
      {!isTerminated && status === 'ON_LEAVE' && (
        <div className="mt-2">
          <Note tone={leaveIsUnpaid ? 'warn' : 'info'}>
            {leaveIsUnpaid
              ? 'Esta persona NO entra en la planilla mientras dure el permiso. Va a aparecer en la previsualización con ese motivo, no desaparece.'
              : 'Con permiso se cobra: esta persona sigue entrando en la planilla del mes. Marcá la casilla solo si el permiso es sin goce de salario.'}
          </Note>
          <div className="mt-2">
            <Field label="Tipo de permiso">
              <label className="row">
                <input
                  type="checkbox"
                  checked={leaveIsUnpaid}
                  onChange={(e) => setLeaveIsUnpaid(e.target.checked)}
                />
                <span>Es un permiso sin goce de salario</span>
              </label>
            </Field>
            {leaveIsUnpaid && (
              <Field label="Motivo" hint="Para saber en tres meses por qué estaba así.">
                <input className="input" value={form.leave_reason} onChange={set('leave_reason')} />
              </Field>
            )}
          </div>
        </div>
      )}

      <div className="grid grid--2">
        <Field label="Cédula"><input className="input" value={form.identification} onChange={set('identification')} /></Field>
        <Field label="Código de empleado"><input className="input mono" value={form.employee_code} onChange={set('employee_code')} /></Field>
      </div>

      <Field label="Ingreso" hint="Con cuidado: mueve el cálculo de vacaciones por ley.">
        <input className="input" type="date" value={form.hire_date} onChange={set('hire_date')} />
      </Field>

      <div className="grid grid--2">
        <Field label="Salario base (₡)" hint="En colones, sin céntimos: 192500 o 192.500.">
          <input className="input input--money" type="text" inputMode="numeric" placeholder="₡0"
            value={form.base_salary} onChange={set('base_salary')} />
        </Field>
        <Field label="Cargas sociales que cubre Clean Blue" hint={COVERAGE_HINT[coverage]}>
          <select className="select" value={coverage}
            onChange={(e) => setCoverage(e.target.value as Coverage)}>
            {Object.entries(COVERAGE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
      </div>

      <Field label="Notas">
        <textarea className="input" rows={3} value={form.notes} onChange={set('notes')} />
      </Field>
    </Modal>
  );
}

function NewEmployeeModal({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState({
    full_name: '', position: '', phone: '', email: '', hire_date: today(), base_salary: '',
  });
  /* El horario se captura acá y no en una segunda vuelta al formulario: una
     persona sin turno declarado es una persona a la que el programador no le
     puede asignar nada, y eso se descubre tarde. Arranca vacío igual —hay
     puestos que no limpian— pero está ofrecido de entrada. */
  const [shifts, setShifts] = useState<ShiftDraft[]>([]);

  const mutation = useApiMutation(
    () => api.post('/admin/employees', {
      full_name: form.full_name,
      position: form.position || undefined,
      phone: form.phone || undefined,
      email: form.email || undefined,
      hire_date: form.hire_date,
      // El campo se llena en colones; el servidor guarda céntimos (§21).
      base_salary: form.base_salary ? parseMoneyInput(form.base_salary) ?? undefined : undefined,
      // Rige desde el ingreso: antes de esa fecha la persona no trabajaba acá,
      // así que no hay pasado que reescribir.
      schedule: shifts.length > 0 ? shifts : undefined,
    }),
    ['/admin/employees'],
    { onSuccess: onClose },
  );
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Modal
      title="Nuevo empleado"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary"
            disabled={!form.full_name || mutation.isPending || !scheduleIsValid(shifts)}
            onClick={() => mutation.mutate()}>Crear</Button>
        </>
      }
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}
      <Field label="Nombre completo" required>
        <input className="input" value={form.full_name} onChange={set('full_name')} autoFocus />
      </Field>
      <div className="grid grid--2">
        <Field label="Puesto"><input className="input" value={form.position} onChange={set('position')} /></Field>
        <Field label="Ingreso" required>
          <input className="input" type="date" value={form.hire_date} onChange={set('hire_date')} />
        </Field>
      </div>
      <div className="grid grid--2">
        <Field label="Teléfono"><input className="input" value={form.phone} onChange={set('phone')} /></Field>
        <Field label="Correo" hint="Le sirve para entrar al portal del empleado.">
          <input className="input" type="email" value={form.email} onChange={set('email')} />
        </Field>
      </div>
      <Field label="Salario base (₡)" hint="En colones, sin céntimos: 192500 o 192.500. Es la base de la planilla y del costo de una hora extra.">
        <input className="input input--money" type="text" inputMode="numeric" placeholder="₡0"
          value={form.base_salary} onChange={set('base_salary')} />
      </Field>
      <Field
        label="Horario de trabajo"
        hint={`Prendé los días que trabaja y agregale los descansos. Rige desde el ${dateLabel(form.hire_date)}.`}
      >
        <ScheduleEditor shifts={shifts} onChange={setShifts} />
      </Field>
    </Modal>
  );
}

/* ── Planilla ───────────────────────────────────────────────────────────────
   El egreso más grande del negocio. Hasta que existió esta pestaña, el margen
   de Finanzas solo restaba insumos y estaba inflado.

   Desde 0020 la pantalla separa **lo que se paga** de **lo que se devenga**, que
   son dos cosas distintas y mostrarlas juntas en un solo número escondería que
   el aguinaldo se paga en diciembre. El costo total es la suma, pero se ve de
   dónde sale.

   La pantalla no calcula nada (regla 2): pide la previsualización al Worker y
   la muestra. El mismo motor es el que asienta al correrla, así que lo que se
   ve acá es exactamente lo que se va a escribir. */

interface PayrollRates {
  employer_rate: number; worker_rate: number;
  christmas_bonus_rate: number; severance_rate: number; vacation_rate: number;
}

/** Los montos de una línea o de un total. Todos son céntimos como texto. */
interface PayrollAmounts {
  gross: string; employer_charge: string; worker_charge: string;
  worker_charge_covered: string;
  /** Lo que SE PAGA este mes: bruto + cargas. */
  cash_cost: string;
  christmas_bonus: string; severance: string; vacation: string;
  /** Lo que SE DEVENGA este mes: aguinaldo + cesantía + vacaciones. */
  provisions: string;
  /** Caja + devengado. */
  total_cost: string;
}

interface PayrollLine extends PayrollAmounts {
  employee_id: string; name: string; position: string | null; coverage: Coverage;
  skip_reason: string | null;
}

interface PayrollTotals extends PayrollAmounts {
  employee_count: number;
}

interface PayrollPreview {
  period_start: string; period_end: string; rates: PayrollRates;
  lines: PayrollLine[]; totals: PayrollTotals; existing_run_id: string | null;
}

interface PayrollRun extends PayrollTotals {
  id: string; period_start: string; period_end: string; status: string;
  created_at: string; triggered_by_name: string | null; rates: PayrollRates;
}

/** `0.2667` → `"26,67 %"`. Es formato de un porcentaje, no aritmética de dinero. */
function ratePct(rate: number): string {
  return `${(rate * 100).toFixed(2).replace('.', ',')} %`;
}

/** `2026-03`. Cualquier otra cosa en la URL se ignora y abre el mes en curso. */
const isMonth = (v: string | null): v is string => v !== null && /^\d{4}-\d{2}$/.test(v);

function PayrollTab({ initialPeriod }: { initialPeriod?: string | null }) {
  const [period, setPeriod] = useState(
    () => (isMonth(initialPeriod ?? null) ? initialPeriod! : today().slice(0, 7)));
  const [confirming, setConfirming] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const preview = useApi<PayrollPreview>(`/admin/payroll/preview?period=${period}`);
  const runs = useApi<{ runs: PayrollRun[] }>('/admin/payroll');

  return (
    <>
      <Card
        title={`Planilla · ${periodLabel(`${period}-01`)}`}
        actions={
          <input
            className="input"
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value || today().slice(0, 7))}
            aria-label="Mes de la planilla"
          />
        }
      >
        <Async query={preview}>
          {(data) => {
            const payable = data.lines.filter((l) => l.skip_reason === null);
            const skipped = data.lines.filter((l) => l.skip_reason !== null);

            return (
              <>
                <Note tone="info">
                  Carga patronal {ratePct(data.rates.employer_rate)} · carga obrera{' '}
                  {ratePct(data.rates.worker_rate)} · aguinaldo{' '}
                  {ratePct(data.rates.christmas_bonus_rate)} · cesantía{' '}
                  {ratePct(data.rates.severance_rate)} · vacaciones{' '}
                  {ratePct(data.rates.vacation_rate)}. Se editan en Configuración y quedan
                  congelados en cada corrida: cambiarlos no altera las planillas ya
                  asentadas. Confirmalos con tu contador antes de la primera.
                </Note>

                {/* Los tres números que contestan «cuánto sale este mes», «cuánto
                    se está acumulando» y «cuánto cuesta de verdad». Separados a
                    propósito: son cosas distintas. */}
                <div className="grid grid--3 mt-2">
                  <Stat
                    label="Sale de caja este mes"
                    value={<Money value={data.totals.cash_cost} size="lg" />}
                    meta={`${data.totals.employee_count} empleados · brutos + cargas`}
                  />
                  <Stat
                    label="Se devenga este mes"
                    value={<Money value={data.totals.provisions} size="lg" />}
                    meta="Aguinaldo, cesantía y vacaciones — se pagan después"
                  />
                  <Stat accent label="Costo total del mes"
                    value={<Money value={data.totals.total_cost} size="lg" />}
                    meta="Lo que se paga más lo que se devenga" />
                </div>

                <div className="mt-2">
                  <Table head={
                    <tr><th>De dónde sale</th><th className="right">Monto</th><th>Cuándo se paga</th></tr>
                  }>
                    <tr>
                      <td>Salarios brutos</td>
                      <td className="right"><Money value={data.totals.gross} /></td>
                      <td className="small muted">Este mes</td>
                    </tr>
                    <tr>
                      <td>Carga patronal</td>
                      <td className="right"><Money value={data.totals.employer_charge} /></td>
                      <td className="small muted">Este mes</td>
                    </tr>
                    <tr>
                      <td>
                        Carga obrera que asume la empresa
                        <span className="tbl__sub">
                          de <Money value={data.totals.worker_charge} /> retenida en total
                        </span>
                      </td>
                      <td className="right"><Money value={data.totals.worker_charge_covered} /></td>
                      <td className="small muted">Este mes</td>
                    </tr>
                    <tr className="strong">
                      <td>Subtotal que sale de caja</td>
                      <td className="right"><Money value={data.totals.cash_cost} /></td>
                      <td className="small muted">Es lo que se asienta en Finanzas</td>
                    </tr>
                    <tr>
                      <td>Aguinaldo devengado</td>
                      <td className="right"><Money value={data.totals.christmas_bonus} /></td>
                      <td className="small muted">Se paga en diciembre</td>
                    </tr>
                    <tr>
                      <td>Cesantía devengada</td>
                      <td className="right"><Money value={data.totals.severance} /></td>
                      <td className="small muted">Al terminar el contrato</td>
                    </tr>
                    <tr>
                      <td>Vacaciones devengadas</td>
                      <td className="right"><Money value={data.totals.vacation} /></td>
                      <td className="small muted">Cuando se toman</td>
                    </tr>
                    <tr className="strong">
                      <td>Costo total del mes</td>
                      <td className="right"><Money value={data.totals.total_cost} /></td>
                      <td className="small muted">Caja + devengado</td>
                    </tr>
                  </Table>
                </div>

                <div className="mt-2">
                  <Note tone="info">
                    <strong>Lo que se paga y lo que se devenga son cosas distintas.</strong>{' '}
                    El aguinaldo se paga en diciembre, pero se gana todos los meses:
                    por eso cuenta como costo del mes aunque no salga plata hoy. En
                    Finanzas se asienta solo lo que sale de caja —
                    <Money value={data.totals.cash_cost} />— porque asentar además lo
                    devengado contaría el mismo aguinaldo dos veces cuando se pague.
                  </Note>
                </div>

                {data.existing_run_id && (
                  <div className="mt-2">
                    <Note tone="warn">
                      Este mes ya fue corrido. Volver a correrlo no duplica nada: el
                      sistema lo rechaza.{' '}
                      <Button onClick={() => setDetailId(data.existing_run_id)}>
                        Ver la corrida
                      </Button>
                    </Note>
                  </div>
                )}

                {skipped.length > 0 && (
                  <div className="mt-2">
                    <Note tone="warn">
                      {skipped.length === 1
                        ? `${skipped[0]!.name} no entra en la planilla: ${skipped[0]!.skip_reason!.toLowerCase()}.`
                        : `${skipped.length} empleados no entran en la planilla. El motivo de cada uno está en su fila.`}
                    </Note>
                  </div>
                )}

                {payable.length === 0 ? (
                  <div className="mt-2">
                    <Empty
                      icon="cobros"
                      title="No hay nada que correr este mes"
                      hint="Ningún empleado activo tiene salario base registrado."
                    />
                  </div>
                ) : (
                  <>
                    <div className="mt-2">
                      <Table head={
                        <tr>
                          <th className="tbl--anchor">Empleado</th><th>Cargas</th><th className="right">Bruto</th>
                          <th className="right">Patronal</th>
                          <th className="right">Asume la empresa</th>
                          <th className="right">Sale de caja</th>
                          <th className="right">Se devenga</th>
                          <th className="right">Costo total</th>
                        </tr>
                      }>
                        {data.lines.map((l) => (
                          <tr key={l.employee_id}>
                            <td className="strong tbl--anchor">
                              <Cell sub={l.skip_reason ?? l.position}>{l.name}</Cell>
                            </td>
                            <td>
                              <Badge
                                tone={COVERAGE_TONE[l.coverage]}
                                title={COVERAGE_HINT[l.coverage]}
                              >
                                {COVERAGE_LABEL[l.coverage]}
                              </Badge>
                            </td>
                            <td className="right"><Money value={l.gross} dim={l.skip_reason !== null} /></td>
                            <td className="right"><Money value={l.employer_charge} dim={l.skip_reason !== null} /></td>
                            <td className="right">
                              <Money value={l.worker_charge_covered} dim={l.coverage !== 'EMPLOYER_AND_WORKER'} />
                            </td>
                            <td className="right"><Money value={l.cash_cost} dim={l.skip_reason !== null} /></td>
                            <td className="right" title="Aguinaldo + cesantía + vacaciones">
                              <Money value={l.provisions} dim={l.skip_reason !== null} />
                            </td>
                            <td className="right strong">
                              <Money value={l.total_cost} dim={l.skip_reason !== null} />
                            </td>
                          </tr>
                        ))}
                      </Table>
                    </div>

                    <div className="mt-2">
                      <Button variant="primary" disabled={data.existing_run_id !== null}
                        onClick={() => setConfirming(true)}>
                        Correr la planilla de {periodLabel(`${period}-01`)}
                      </Button>
                    </div>
                  </>
                )}
              </>
            );
          }}
        </Async>
      </Card>

      <div className="mt-3">
        <Async query={runs} empty={(d) => d.runs.length === 0 && (
          <Empty icon="reportes" title="Todavía no se ha corrido ninguna planilla"
            hint="Las corridas quedan acá, con el desglose congelado de cada mes." />
        )}>
          {(data) => (
            <Card title="Corridas" flush>
              <Table head={
                <tr>
                  <th className="tbl--anchor">Período</th><th className="right">Empleados</th><th className="right">Bruto</th>
                  <th className="right">Sale de caja</th><th className="right">Se devenga</th>
                  <th className="right">Costo total</th>
                  <th>Corrida por</th><th />
                </tr>
              }>
                {data.runs.map((r) => (
                  <tr key={r.id}>
                    <td className="strong tbl--anchor">{periodLabel(r.period_start)}</td>
                    <td className="right">{r.employee_count}</td>
                    <td className="right"><Money value={r.gross} /></td>
                    <td className="right"><Money value={r.cash_cost} /></td>
                    <td className="right"><Money value={r.provisions} dim={r.provisions === '0'} /></td>
                    <td className="right strong"><Money value={r.total_cost} /></td>
                    <td className="small muted">
                      <Cell sub={dateLabel(r.created_at.slice(0, 10))}>
                        {r.triggered_by_name ?? '—'}
                      </Cell>
                    </td>
                    <td className="right">
                      <Button onClick={() => setDetailId(r.id)}>Ver desglose</Button>
                    </td>
                  </tr>
                ))}
              </Table>
            </Card>
          )}
        </Async>
      </div>

      {confirming && preview.data && (
        <RunPayrollModal period={period} totals={preview.data.totals}
          onClose={() => setConfirming(false)} />
      )}
      {detailId && <PayrollDetailModal runId={detailId} onClose={() => setDetailId(null)} />}
    </>
  );
}

/**
 * Confirmar la corrida.
 *
 * Es dinero que entra al ledger, y el ledger es append-only: un error no se
 * borra, se compensa con el asiento contrario. Por eso hay un paso más antes
 * del botón, con el desglose a la vista.
 */
function RunPayrollModal({ period, totals, onClose }: {
  period: string; totals: PayrollTotals; onClose: () => void;
}) {
  const mutation = useApiMutation(
    () => api.post('/admin/payroll/run', { period }),
    ['/admin/payroll', '/admin/transactions', '/admin/reports'],
    { onSuccess: onClose },
  );

  return (
    <Modal
      title={`Correr la planilla de ${periodLabel(`${period}-01`)}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Corriendo…' : 'Correr y asentar'}
          </Button>
        </>
      }
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}
      <Note tone="warn">
        Se asienta un egreso de <Money value={totals.cash_cost} /> por{' '}
        {totals.employee_count} empleados con fecha del último día del mes. El ledger
        no se edita: si algo sale mal se corrige con el asiento contrario, no borrando
        este.
      </Note>
      <div className="mt-2">
        <Table head={<tr><th>Concepto</th><th className="right">Monto</th></tr>}>
          <tr><td>Salarios brutos</td><td className="right"><Money value={totals.gross} /></td></tr>
          <tr><td>Carga patronal</td><td className="right"><Money value={totals.employer_charge} /></td></tr>
          <tr>
            <td>Carga obrera que asume la empresa</td>
            <td className="right"><Money value={totals.worker_charge_covered} /></td>
          </tr>
          <tr className="strong">
            <td>Sale de caja — es lo que se asienta</td>
            <td className="right"><Money value={totals.cash_cost} /></td>
          </tr>
          <tr>
            <td>Aguinaldo, cesantía y vacaciones devengados</td>
            <td className="right"><Money value={totals.provisions} /></td>
          </tr>
          <tr className="strong">
            <td>Costo total del mes</td><td className="right"><Money value={totals.total_cost} /></td>
          </tr>
        </Table>
      </div>
      <div className="mt-2">
        <Note tone="info">
          Lo devengado queda guardado en la corrida —con las tasas del momento— pero
          no se asienta en Finanzas: se paga después, y asentarlo ahora contaría el
          mismo aguinaldo dos veces.
        </Note>
      </div>
    </Modal>
  );
}

/** El desglose congelado de una corrida: lo que se pagó, con las tasas de entonces. */
function PayrollDetailModal({ runId, onClose }: { runId: string; onClose: () => void }) {
  const detail = useApi<{ run: PayrollRun; lines: PayrollLine[] }>(`/admin/payroll/${runId}`);

  return (
    <Modal title="Desglose de la planilla" onClose={onClose} wide
      footer={<Button onClick={onClose}>Cerrar</Button>}>
      <Async query={detail}>
        {(data) => (
          <>
            <Note tone="info">
              {periodLabel(data.run.period_start)} · patronal{' '}
              {ratePct(data.run.rates.employer_rate)} · obrera{' '}
              {ratePct(data.run.rates.worker_rate)} · aguinaldo{' '}
              {ratePct(data.run.rates.christmas_bonus_rate)} · cesantía{' '}
              {ratePct(data.run.rates.severance_rate)} · vacaciones{' '}
              {ratePct(data.run.rates.vacation_rate)}. Son los porcentajes que regían
              cuando se corrió, congelados en la fila.
            </Note>
            <div className="mt-2">
              <Table
                head={
                  <tr>
                    <th className="tbl--anchor">Empleado</th><th>Cargas</th><th className="right">Bruto</th>
                    <th className="right">Patronal</th><th className="right">Asume la empresa</th>
                    <th className="right">Sale de caja</th><th className="right">Se devenga</th>
                    <th className="right">Costo</th>
                  </tr>
                }
                foot={
                  <tr className="strong">
                    <td colSpan={2}>Total</td>
                    <td className="right"><Money value={data.run.gross} /></td>
                    <td className="right"><Money value={data.run.employer_charge} /></td>
                    <td className="right"><Money value={data.run.worker_charge_covered} /></td>
                    <td className="right"><Money value={data.run.cash_cost} /></td>
                    <td className="right"><Money value={data.run.provisions} /></td>
                    <td className="right"><Money value={data.run.total_cost} /></td>
                  </tr>
                }
              >
                {data.lines.map((l) => (
                  <tr key={l.employee_id}>
                    <td className="strong tbl--anchor"><Cell sub={l.position}>{l.name}</Cell></td>
                    <td>
                      <Badge tone={COVERAGE_TONE[l.coverage]}>
                        {COVERAGE_LABEL[l.coverage]}
                      </Badge>
                    </td>
                    <td className="right"><Money value={l.gross} /></td>
                    <td className="right"><Money value={l.employer_charge} /></td>
                    <td className="right">
                      <Money value={l.worker_charge_covered} dim={l.coverage !== 'EMPLOYER_AND_WORKER'} />
                    </td>
                    <td className="right"><Money value={l.cash_cost} /></td>
                    <td className="right" title="Aguinaldo + cesantía + vacaciones">
                      <Money value={l.provisions} dim={l.provisions === '0'} />
                    </td>
                    <td className="right strong"><Money value={l.total_cost} /></td>
                  </tr>
                ))}
              </Table>
            </div>
            <div className="mt-2">
              <Note tone="info">
                En Finanzas se asentó <Money value={data.run.cash_cost} />, que es lo
                que salió de caja. Las provisiones quedan registradas acá y se asientan
                cuando se paguen.
              </Note>
            </div>
          </>
        )}
      </Async>
    </Modal>
  );
}
